// View / download / print for a stored document, given a short-lived signed URL.
//
// The URLs are cross-origin (object storage), which rules out the usual
// `window.open(url).print()` trick: the load event does not fire reliably on another origin,
// so the print dialog either never opens or opens on a blank page. Fetching the file as a
// blob first puts it on our own origin, where printing behaves.

const FILENAME_FALLBACK = "attachment";

/** Human file size — "2.4 MB", "812 KB". */
export const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const isPdf = (mime, filename) =>
  String(mime || "").includes("pdf") || /\.pdf$/i.test(String(filename || ""));

const isImage = (mime, filename) =>
  String(mime || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(String(filename || ""));

/**
 * True when the shared DocumentPreviewer can render this inline — PDFs, images, Office
 * documents and video (its renderers' extension lists) — so the preview modal opens for
 * anything it can show. Printing stays narrower: `printFile` below prints only what the
 * BROWSER renders (pdf/image) and downloads the rest.
 */
export const isPreviewable = (mime, filename) => {
  if (isPdf(mime, filename) || isImage(mime, filename)) return true;
  const f = String(filename || "").toLowerCase();
  const m = String(mime || "").toLowerCase();
  if (/\.(tiff?|docx?|pptx?|xlsx?|odt|mp4|webm|ogg)$/.test(f)) return true;
  return m.startsWith("video/") || m.includes("officedocument") || m.includes("msword") || m.includes("ms-excel") || m.includes("ms-powerpoint") || m.includes("opendocument");
};

/**
 * The file name accounting sees: "Unit # - 103 The Vibe (New PAD - 2026-09-01)".
 * `originalName` contributes only its extension; characters a filesystem refuses are
 * replaced. Used for every download and print of a PAD document, and (without an
 * extension) as the unit history print's document title.
 */
export function padFileName({ unitNo, propertyName, categoryLabel, date, originalName }) {
  const ext = /\.[a-z0-9]{1,8}$/i.exec(String(originalName || ""))?.[0] || "";
  const base = `Unit # - ${unitNo || ""} ${propertyName || ""} (${categoryLabel || "PAD"} - ${date || ""})`
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  return `${base}${ext}`;
}

/** Open in a new tab. Falls back to a same-tab navigation when popups are blocked. */
export function openFile(url) {
  if (!url) return;
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (!w) window.location.href = url;
}

/** Save to disk under its real filename. */
export async function downloadFile(url, filename) {
  if (!url) return;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not fetch the file (${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename || FILENAME_FALLBACK;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick — Safari needs the URL to survive the click.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Print a document. PDFs and images are fetched to a blob and printed from a same-origin
 * window; anything else the browser cannot render is downloaded instead, because a print
 * dialog over a file the browser cannot display is a dead end.
 */
export async function printFile(url, filename, mime) {
  if (!url) return;
  // Only what the browser itself renders can be printed — pdf and images. The wider
  // isPreviewable set (Office, video) previews through the DocumentPreviewer but has no
  // printable page, so it downloads instead.
  if (!isPdf(mime, filename) && !isImage(mime, filename)) {
    await downloadFile(url, filename);
    return;
  }

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not fetch the file (${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);

  const win = window.open("", "_blank");
  if (!win) {
    // Popups blocked — hand the file over rather than failing silently.
    URL.revokeObjectURL(objectUrl);
    await downloadFile(url, filename);
    return;
  }

  const cleanup = () => setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);

  if (isImage(mime, filename)) {
    // An image needs a page around it; wait for decode before printing or it prints blank.
    win.document.write(
      `<html><head><title>${escapeHtml(filename || FILENAME_FALLBACK)}</title>` +
        `<style>@page{margin:12mm}body{margin:0}img{max-width:100%;height:auto;display:block}</style>` +
        `</head><body><img src="${objectUrl}" alt="${escapeHtml(filename || "")}" /></body></html>`
    );
    win.document.close();
    const img = win.document.querySelector("img");
    if (img) {
      img.onload = () => {
        win.focus();
        win.print();
        cleanup();
      };
    }
    return;
  }

  // PDF: the viewer prints itself once loaded.
  win.location.href = objectUrl;
  win.onload = () => {
    win.focus();
    win.print();
    cleanup();
  };
  // Some browsers never fire onload for the built-in PDF viewer; nudge it after a moment.
  setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      /* the window may already be gone — nothing to do */
    }
    cleanup();
  }, 1200);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
